import React, { useState, useEffect } from "react";
import {
    Container,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Grid,
    Box,
    Chip,
    IconButton,
    Divider,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Tooltip,
    Stack
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import { Cancel, Close, Save } from "@mui/icons-material";
import { createTheme, ThemeProvider } from '@mui/material/styles';

const API_BASE_URL = "http://155.133.27.173:3020"; // Update if needed


const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f5f5f5',
        },
    },
    typography: {
        h4: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 500,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                },
            },
        },
    },
});

const ShiftPlanManager = () => {
    const [shiftPlans, setShiftPlans] = useState([]);
    const [view, setView] = useState("table"); // "table", "create"
    const [selectedDays, setSelectedDays] = useState(["Monday"]); // Default to Monday
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    const [formData, setFormData] = useState({
        name: "",
        department: "",
        designation: "",
        company: "",
        shiftType: "Fixed",
        userID: localStorage.getItem("userID"),
        fixedShift: {},
        flexibleShift: {},
    });
    // Options for dropdowns
    const [companyOptions, setCompanyOptions] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [deviceOptions, setDeviceOptions] = useState([]);

    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday", "Everyday"];

    // Helper function to fetch data from API
    const fetchData = async (endpoint, stateSetter) => {

        const token = localStorage.getItem('token');
        const userID = localStorage.getItem('userID');
        const headers = { 'Authorization': `Bearer ${token}` };
        try {
            const response = await fetch(`${API_BASE_URL}/${endpoint}/${userID}`, { headers });
            const data = await response.json();
            if (data.length > 0) {
                stateSetter(data);
            }
        } catch (error) {
            console.error(`Error fetching ${endpoint}:`, error);
        }
    };
    // Fetch all necessary data on component mount
    useEffect(() => {
        fetchData('all-shift-plan', setShiftPlans);
        fetchData('get-company', setCompanyOptions);
        fetchData('get-departments', setDepartmentOptions);
        fetchData('get-designation', setDesignationOptions);
        fetchData('get-devices', setDeviceOptions);
    }, []);

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Handle form changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Handle time change for Fixed Shift using standard inputs
    const handleTimeChange = (day, type, e) => {
        const timeValue = e.target.value;
        setFormData((prev) => ({
            ...prev,
            fixedShift: {
                ...prev.fixedShift,
                [day]: { ...prev.fixedShift[day], [type]: timeValue },
            },
        }));
    };

    // Handle hour change for Flexible Shift
    const handleHourChange = (day, newValue) => {
        setFormData((prev) => ({
            ...prev,
            flexibleShift: {
                ...prev.flexibleShift,
                [day]: { duration: newValue },
            },
        }));
    };

    // Handle days selection
    const handleDaysChange = (e) => {
        const value = e.target.value;
        if (value.includes("Everyday")) {
            setSelectedDays(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]);
        } else {
            setSelectedDays(value);
        }
    };

    // Reset form data
    const resetForm = () => {
        setFormData({
            name: "",
            department: "",
            designation: "",
            company: "",
            userID: localStorage.getItem("userID"),
            shiftType: "Fixed",
            fixedShift: {},
            flexibleShift: {},
        });
        setSelectedDays(["Monday"]);
        setView("table");
    };

    // Handle shift creation
    const handleCreateShift = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/create-shift-plan`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (!response.ok) throw new Error("Failed to create shift plan");

            showSnackbar("Shift plan created successfully");
            resetForm();
            fetchData('all-shift-plan', setShiftPlans);
        } catch (error) {
            console.error("Error creating shift plan:", error);
            showSnackbar("Failed to create shift plan", "error");
        }
    };

    // Handle shift deletion
    const handleDeleteShift = async (id) => {
        if (!window.confirm("Are you sure you want to delete this shift plan?")) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/delete-shift-plan/${id}`, {
                method: "DELETE",
            });

            if (!response.ok) throw new Error("Failed to delete shift plan");

            setShiftPlans(shiftPlans.filter((shift) => shift._id !== id));
            showSnackbar("Shift plan deleted successfully");
        } catch (error) {
            console.error("Error deleting shift plan:", error);
            showSnackbar("Failed to delete shift plan", "error");
        }
    };

    // Format time for display
    const formatTime = (timeString) => {
        if (!timeString) return "Not set";
        return timeString;
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    // Render shift plan table
    const renderShiftPlanTable = () => (<TableContainer component={Paper} elevation={2} sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Table aria-label="department table">
            <TableHead>
                <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Shift Name</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Shift Type</TableCell>
                    <TableCell>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {shiftPlans.length > 0 ? (
                    shiftPlans.map((shift) => (
                        <TableRow key={shift._id} hover>
                            <TableCell>{shift._id?.substring(0, 8)}...</TableCell>
                            <TableCell>
                                <Box display="flex" alignItems="center">
                                    <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                                    {shift.name || "N/A"}
                                </Box>
                            </TableCell>
                            <TableCell>{shift.department?.name || "N/A"}</TableCell>
                            <TableCell>
                                <Box display="flex" alignItems="center">
                                    <BusinessIcon sx={{ mr: 1, color: "primary.main" }} />
                                    {shift.company?.name || "N/A"}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={shift.shiftType}
                                    color={shift.shiftType === "Fixed" ? "primary" : "secondary"}
                                    size="small"
                                    icon={<AccessTimeIcon />}
                                />
                            </TableCell>
                            <TableCell>
                                <Tooltip title="Delete">
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteShift(shift._id)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))
                ) : (
                    <TableRow>
                        <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                {loading ? "Loading shift plans..." : "No shift plans found. Create your first one!"}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
    );
    // Render shift plan form
    const renderShiftPlanForm = () => (
        <Card elevation={3}>
            <CardContent>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Name"
                            name="name"
                            fullWidth
                            required
                            variant="outlined"
                            value={formData.name}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Company"
                            name="company"
                            variant="outlined"
                            value={formData.company}
                            onChange={handleFormChange}
                            required>
                            {companyOptions.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Department"
                            name="department"
                            variant="outlined"
                            value={formData.department}
                            onChange={handleFormChange}
                            required
                        >
                            {departmentOptions.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Designation"
                            name="designation"
                            variant="outlined"
                            value={formData.designation}
                            onChange={handleFormChange}
                            required
                        >
                            {designationOptions.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>

                <Box mt={3}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Shift Type</InputLabel>
                        <Select
                            name="shiftType"
                            value={formData.shiftType}
                            onChange={handleChange}
                            label="Shift Type"
                        >
                            <MenuItem value="Fixed">Fixed (Specific times)</MenuItem>
                            <MenuItem value="Flexible">Flexible (Hour-based)</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box mt={3}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Work Days</InputLabel>
                        <Select
                            multiple
                            value={selectedDays}
                            onChange={handleDaysChange}
                            label="Work Days"
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {daysOfWeek.map((day) => (
                                <MenuItem key={day} value={day}>
                                    {day}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {formData.shiftType === "Fixed" && (
                    <Box mt={3}>
                        <Typography variant="h6" gutterBottom>
                            Shift Timings
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {selectedDays.filter(day => day !== "Everyday").map((day) => (
                            <Grid container spacing={2} key={day} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle1">{day}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Check-in"
                                        type="time"
                                        fullWidth
                                        value={formData.fixedShift[day]?.checkIn || ""}
                                        onChange={(e) => handleTimeChange(day, "checkIn", e)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 300 }} // 5min intervals
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Check-out"
                                        type="time"
                                        fullWidth
                                        value={formData.fixedShift[day]?.checkOut || ""}
                                        onChange={(e) => handleTimeChange(day, "checkOut", e)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 300 }} // 5min intervals
                                    />
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                )}

                {formData.shiftType === "Flexible" && (
                    <Box mt={3}>
                        <Typography variant="h6" gutterBottom>
                            Required Hours
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {selectedDays.filter(day => day !== "Everyday").map((day) => (
                            <Grid container spacing={2} key={day} sx={{ mb: 2 }} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle1">{day}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        label="Working Hours"
                                        type="number"
                                        fullWidth
                                        variant="outlined"
                                        value={formData.flexibleShift[day]?.duration || ""}
                                        InputProps={{ inputProps: { min: 0, max: 24, step: 0.5 } }}
                                        onChange={(e) => handleHourChange(day, e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={resetForm}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleCreateShift}
                        disabled={!formData.name}
                    >
                        Create Shift Plan
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );

    return (<ThemeProvider theme={theme}>
        <Container maxWidth="lg"
            sx={{
                maxHeight: '80vh', // Set a maximum height instead of fixed height
                overflowY: 'auto', // Enable vertical scrolling
                width: '100%',
                py: 2
            }}>
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" color="primary">
                        {view === 'table' && 'Shift Plan'}
                        {view === 'create' && 'Create Shift Plan'}
                        {view === 'edit' && 'Edit Shift Plan'}
                    </Typography>

                    {view === 'table' ? <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setView("create")}
                    >
                        Add Shift Plan
                    </Button> : <Button
                        variant="contained"
                        color="warning"
                        startIcon={<Close />}
                        onClick={resetForm}>
                        Cancel
                    </Button>}
                </Box>
                <Divider sx={{ mt: 2 }} />
            </Box>

            {view === "table" && renderShiftPlanTable()}
            {view === "create" && renderShiftPlanForm()}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    </ThemeProvider>);
};

export default ShiftPlanManager;